import React, { useCallback, useEffect, useRef } from "react";
import useAudioPeaks, { resample } from "./useAudioPeaks";
import {
  BAR_WIDTH,
  MIN_BAR_HEIGHT,
  WAVE_COLOR,
  PLAYED_COLOR,
  FALLBACK_COLOR,
  FALLBACK_PLAYED_COLOR,
  barsCountForWidth,
} from "./waveConfig";
import "./VoiceRecorder.css";

const VoiceWaveTrack = ({
  audioRef,
  src,
  peaks = null,
  progress = 0,
  onSeek = () => {},
  className = "",
}) => {
  const canvasRef = useRef(null);
  const { peaks: bars, isFallback } = useAudioPeaks(src, peaks);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const cssWidth = canvas.clientWidth || 220;
    const cssHeight = canvas.clientHeight || 28;
    canvas.width = Math.max(1, Math.floor(cssWidth * dpr));
    canvas.height = Math.max(1, Math.floor(cssHeight * dpr));
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cssWidth, cssHeight);
    if (!bars.length) {
      ctx.fillStyle = "#333b4b";
      ctx.fillRect(0, cssHeight / 2 - 1, cssWidth, 2);
      return;
    }

    const count = barsCountForWidth(cssWidth);
    const scaled = resample(bars, count);
    const step = cssWidth / count;
    const playedX = progress * cssWidth;

    scaled.forEach((value, index) => {
      const h = Math.max(MIN_BAR_HEIGHT, value * cssHeight);
      const x = index * step;
      const y = (cssHeight - h) / 2;
      ctx.fillStyle = isFallback
        ? x + BAR_WIDTH / 2 <= playedX
          ? FALLBACK_PLAYED_COLOR
          : FALLBACK_COLOR
        : x + BAR_WIDTH / 2 <= playedX
          ? PLAYED_COLOR
          : WAVE_COLOR;
      ctx.fillRect(x, y, BAR_WIDTH, h);
    });
  }, [bars, progress, isFallback]);

  const seekFromClientX = useCallback(
    (clientX) => {
      const audio = audioRef && audioRef.current;
      const canvas = canvasRef.current;
      if (!audio || !canvas || !audio.duration) return;
      const rect = canvas.getBoundingClientRect();
      const ratio = Math.min(
        1,
        Math.max(0, (clientX - rect.left) / rect.width)
      );
      audio.currentTime = ratio * audio.duration;
      onSeek(ratio);
    },
    [audioRef, onSeek]
  );

  const handlePointerDown = (e) => {
    seekFromClientX(e.clientX);
    const onMove = (event) => seekFromClientX(event.clientX);
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  return (
    <canvas
      ref={canvasRef}
      className={`voice-wave-player-canvas ${className}`}
      onPointerDown={handlePointerDown}
    />
  );
};

export default VoiceWaveTrack;
